/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { lobPath } from '@airasia-rn/web/utils/localePath';
import { PRODUCT_TILE_SLUGS } from '../constants/productSlugs';

/**
 * Warms the Next.js client cache for home plus each product-tile route so
 * navigation is instant. Call it from a page client (e.g. HomePageClient) that
 * re-runs on every route change; pass the currently-active slug so it can be
 * skipped — Next won't cache the page you're already on, so the route you land
 * on is warmed once you move off it instead of re-fetching every visit.
 *
 * Note: `router.prefetch` is a no-op in development — prefetch RSC requests
 * (`?_rsc=`) only fire in a production build.
 *
 * @param locale           Active locale identifier (e.g. `en-gb`); mapped to its
 *                         two-segment URL path form (e.g. `en/gb`) for hrefs.
 * @param searchWidgetSlug The current product-tile slug, or `undefined` on home.
 */
export const usePrefetchProductRoutes = (
  locale: string,
  searchWidgetSlug?: string,
) => {
  const router = useRouter();

  useEffect(() => {
    // Prefetch the PUBLIC (LOB-first) URLs — the router cache is keyed on what
    // the user actually navigates to, not on the rewritten route.
    const activeRoute = lobPath(locale, searchWidgetSlug);

    const routes = [
      lobPath(locale),
      ...PRODUCT_TILE_SLUGS.map((slug) => lobPath(locale, slug)),
    ];

    routes.forEach((href) => {
      if (href === activeRoute) return;
      router.prefetch(href);
    });
  }, [locale, router, searchWidgetSlug]);
};
